<template>
  <layout-page
    :anchors="[{ path: 'privacy_policy', label: $t('links.page_privacy_policy', 'Privacy Policy') }]"
    :activeSection="'privacy_policy'"
  >
    <section ref="heroSection" class="page-hero">
      <p class="page-hero__eyebrow">{{ $t('privacy.eyebrow', 'Legal') }}</p>
      <h1 class="page-hero__title">{{ $t('privacy.title', 'Privacy Policy') }}</h1>
      <p class="page-hero__date">{{ $t('privacy.effective_date', 'Effective Date: May 2026') }}</p>
    </section>

    <div class="layout">
      <article>
        <p class="article__intro">
          {{ $t('privacy.intro', 'ParcelPoint ("Platform," "we," "our," or "us") respects your privacy and is committed to protecting information collected through our website, operational systems, and logistics workflow services. This Privacy Policy explains how information may be collected, used, stored, and protected when using ParcelPoint.us and related operational services.') }}
        </p>

        <div class="article__section" id="s1">
          <h2>{{ $t('privacy.s1_title', 'Information We Collect') }}</h2>
          <p>{{ $t('privacy.s1_text', 'We may collect operational and technical information including:') }}</p>
          <ul>
            <li>{{ $t('privacy.s1_item1', 'Name, email address, phone number, and account information;') }}</li>
            <li>{{ $t('privacy.s1_item2', 'Shipment details, tracking information, package intake records, and warehouse workflow data;') }}</li>
            <li>{{ $t('privacy.s1_item3', 'Communications submitted through forms, support requests, or operational inquiries;') }}</li>
            <li>{{ $t('privacy.s1_item4', 'Technical information including browser type, device information, IP address, cookies, and usage analytics.') }}</li>
          </ul>
        </div>
        <hr class="article__divider" />

        <div class="article__section" id="s2">
          <h2>{{ $t('privacy.s2_title', 'How Information May Be Used') }}</h2>
          <p>{{ $t('privacy.s2_text', 'Information may be used to:') }}</p>
          <ul>
            <li>{{ $t('privacy.s2_item1', 'Support shipment intake and warehouse workflows;') }}</li>
            <li>{{ $t('privacy.s2_item2', 'Coordinate logistics-related operational activities;') }}</li>
            <li>{{ $t('privacy.s2_item3', 'Provide customer support and communication;') }}</li>
            <li>{{ $t('privacy.s2_item4', 'Improve platform functionality and operational performance;') }}</li>
            <li>{{ $t('privacy.s2_item5', 'Maintain platform security and prevent unauthorized access;') }}</li>
            <li>{{ $t('privacy.s2_item6', 'Comply with applicable legal or operational requirements.') }}</li>
          </ul>
        </div>
        <hr class="article__divider" />

        <div class="article__section" id="s3">
          <h2>{{ $t('privacy.s3_title', 'Information Sharing') }}</h2>
          <p>{{ $t('privacy.s3_text1', 'ParcelPoint does not sell personal information.') }}</p>
          <p>{{ $t('privacy.s3_text2', 'Information may be shared with affiliated operational systems, logistics providers, warehouse partners, shipment carriers, and technology infrastructure providers as reasonably necessary to support logistics workflows and operational coordination.') }}</p>
        </div>
        <hr class="article__divider" />

        <div class="article__section" id="s4">
          <h2>{{ $t('privacy.s4_title', 'Data Security') }}</h2>
          <p>{{ $t('privacy.s4_text1', 'Reasonable technical and organizational safeguards are used to help protect operational and user information from unauthorized access, disclosure, or misuse.') }}</p>
          <p>{{ $t('privacy.s4_text2', 'However, no electronic system can be guaranteed to be completely secure.') }}</p>
        </div>
        <hr class="article__divider" />

        <div class="article__section" id="s5">
          <h2>{{ $t('privacy.s5_title', 'Cookies and Analytics') }}</h2>
          <p>{{ $t('privacy.s5_text1', 'ParcelPoint may use cookies, analytics tools, and related technologies to improve operational performance and website functionality.') }}</p>
          <p>{{ $t('privacy.s5_text2', 'Users may disable cookies through browser settings, although certain features may become limited.') }}</p>
        </div>
        <hr class="article__divider" />

        <div class="article__section" id="s6">
          <h2>{{ $t('privacy.s6_title', 'Third-Party Services') }}</h2>
          <p>{{ $t('privacy.s6_text', 'The platform may contain integrations or links to third-party operational systems, carriers, or logistics-related services. ParcelPoint is not responsible for third-party privacy practices or external systems.') }}</p>
        </div>
        <hr class="article__divider" />

        <div class="article__section" id="s7">
          <h2>{{ $t('privacy.s7_title', 'Data Retention') }}</h2>
          <p>{{ $t('privacy.s7_text', 'Information may be retained for operational, security, legal, and recordkeeping purposes for a commercially reasonable period of time.') }}</p>
        </div>
        <hr class="article__divider" />

        <div class="article__section" id="s8">
          <h2>{{ $t('privacy.s8_title', 'Policy Changes') }}</h2>
          <p>{{ $t('privacy.s8_text', 'This Privacy Policy may be updated periodically. Updated versions will be posted on this page with a revised effective date.') }}</p>
        </div>
        <hr class="article__divider" />

        <div class="article__section" id="s9">
          <h2>{{ $t('privacy.s9_title', 'Contact Information') }}</h2>
          <div class="contact-card">
            <p><strong>{{ $t('privacy.houston_office', 'Houston Operations Office') }}</strong></p>
            <p>5373 West Alabama St, Ste 590</p>
            <p>Houston, Texas 77056, United States</p>
            <p style="margin-top:14px;">
              {{ $t('privacy.email_label', 'Email:') }}
              <a href="mailto:ceo@parcelpoint.us">ceo@parcelpoint.us</a>
            </p>
            <p>
              {{ $t('privacy.phone_label', 'Phone:') }}
              <a href="tel:+15127853401">+1 (512) 785-3401</a>
            </p>
            <p class="contact-card__note">
              {{ $t('privacy.note', 'ParcelPoint is an affiliated logistics technology platform used in connection with StockFore Inc. operations.') }}
            </p>
          </div>
        </div>
      </article>

      <aside ref="tocWrapper" class="toc-wrapper">
        <nav ref="toc" class="toc" :class="{ 'toc--visible': isVisible, 'toc--hidden': !isVisible }" :style="tocStyle">
          <p class="toc__title">{{ $t('privacy.toc_title', 'Contents') }}</p>
          <ol class="toc__list">
            <li :class="{ active: activeSection === 's1' }">
              <a href="#" @click.prevent="scrollToSection('s1')">1. {{ $t('privacy.s1_title', 'Information We Collect') }}</a>
            </li>
            <li :class="{ active: activeSection === 's2' }">
              <a href="#" @click.prevent="scrollToSection('s2')">2. {{ $t('privacy.s2_title', 'How Information May Be Used') }}</a>
            </li>
            <li :class="{ active: activeSection === 's3' }">
              <a href="#" @click.prevent="scrollToSection('s3')">3. {{ $t('privacy.s3_title', 'Information Sharing') }}</a>
            </li>
            <li :class="{ active: activeSection === 's4' }">
              <a href="#" @click.prevent="scrollToSection('s4')">4. {{ $t('privacy.s4_title', 'Data Security') }}</a>
            </li>
            <li :class="{ active: activeSection === 's5' }">
              <a href="#" @click.prevent="scrollToSection('s5')">5. {{ $t('privacy.s5_title', 'Cookies and Analytics') }}</a>
            </li>
            <li :class="{ active: activeSection === 's6' }">
              <a href="#" @click.prevent="scrollToSection('s6')">6. {{ $t('privacy.s6_title', 'Third-Party Services') }}</a>
            </li>
            <li :class="{ active: activeSection === 's7' }">
              <a href="#" @click.prevent="scrollToSection('s7')">7. {{ $t('privacy.s7_title', 'Data Retention') }}</a>
            </li>
            <li :class="{ active: activeSection === 's8' }">
              <a href="#" @click.prevent="scrollToSection('s8')">8. {{ $t('privacy.s8_title', 'Policy Changes') }}</a>
            </li>
            <li :class="{ active: activeSection === 's9' }">
              <a href="#" @click.prevent="scrollToSection('s9')">9. {{ $t('privacy.s9_title', 'Contact Information') }}</a>
            </li>
          </ol>
        </nav>
      </aside>
    </div>
  </layout-page>
</template>

<script>
import LayoutPage from '@/components/LayoutPage.vue';

export default {
  name: 'PrivacyPolicy',
  components: { LayoutPage },
  data() {
    return {
      activeSection: 's1',
      isVisible: false,
      tocStyle: {
        position: 'fixed',
        top: '80px',
        right: '100px',     // ← здесь задаёте отступ от правого края
        width: '220px',
      },
      sectionIds: ['s1', 's2', 's3', 's4', 's5', 's6', 's7', 's8', 's9'],
      observer: null,
      footerElement: null,
      originalTop: 80,
    };
  },
  mounted() {
    document.body.classList.add('privacy-policy-page');
    this.initObserver();
    this.footerElement = document.querySelector('footer');
    this.calculateOriginalPosition();
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize);
    this.handleScroll();
  },
  beforeDestroy() {
    document.body.classList.remove('privacy-policy-page');
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 80;
        const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top, behavior: 'smooth' });
        this.activeSection = sectionId;
      }
    },
    initObserver() {
      const options = { rootMargin: '-20% 0px -60% 0px', threshold: 0 };
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            if (id && this.sectionIds.includes(id)) this.activeSection = id;
          }
        });
      }, options);
      this.sectionIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) this.observer.observe(el);
      });
    },
    calculateOriginalPosition() {
      // Создаём временный элемент, чтобы узнать, где должно быть меню в потоке
      const dummy = document.createElement('div');
      dummy.style.width = '220px';
      dummy.style.height = '1px';
      dummy.style.position = 'absolute';
      dummy.style.right = '40px'; // такой же отступ, как у фиксированного меню
      dummy.style.top = '0';
      document.querySelector('.layout')?.appendChild(dummy);
      const rect = dummy.getBoundingClientRect();
      this.originalTop = rect.top + window.pageYOffset;
      dummy.remove();
    },
    handleScroll() {
      const scrollTop = window.pageYOffset;
      const tocHeight = 400; // примерная высота меню
      const shouldShow = scrollTop + 80 > this.originalTop + tocHeight;

      let willHitFooter = false;
      if (this.footerElement) {
        const footerRect = this.footerElement.getBoundingClientRect();
        const footerTop = footerRect.top + scrollTop;
        const tocBottomWhenFixed = scrollTop + 80 + tocHeight;
        if (tocBottomWhenFixed + 20 > footerTop) {
          willHitFooter = true;
        }
      }

      this.isVisible = shouldShow && !willHitFooter && window.innerWidth > 900;
    },
    handleResize() {
      this.calculateOriginalPosition();
      this.handleScroll();
    },
  },
};
</script>

<style scoped>
.page-hero {
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  background: #111;
  padding: 72px 40px 60px;
  text-align: center;
}
.page-hero__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #e63329;
  margin-bottom: 14px;
}
.page-hero__title {
  font-size: 48px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.04em;
  margin-bottom: 14px;
  line-height: 1.1;
}
.page-hero__date {
  font-size: 14px;
  color: #666;
}
.layout {
  max-width: 1000px;
  margin: 0 auto;
  padding: 64px 40px 96px;
  display: grid;
  grid-template-columns: 1fr 220px;
  gap: 64px;
  align-items: start;
}
.toc-wrapper {
  position: relative;
  display: flex;
}
.toc {
  background: #f8f8f8;
  border-radius: 12px;
  padding: 22px 20px;
  transition: opacity 0.2s, visibility 0.2s;
  box-shadow: 0 8px 20px rgba(0,0,0,0.1);
  z-index: 100;
  opacity: 0;
  visibility: hidden;
}
.toc--visible {
  opacity: 1;
  visibility: visible;
}
.toc--hidden {
  opacity: 0;
  visibility: hidden;
}
.toc--fixed {
  box-shadow: 0 8px 20px rgba(0,0,0,0.1);
  z-index: 100;
}
.toc__title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #999;
  margin-bottom: 14px;
}
.toc__list {
  list-style: none;
  padding: 0;
}
.toc__list li {
  margin-bottom: 9px;
}
.toc__list a {
  font-size: 13px;
  color: #555;
  text-decoration: none;
  line-height: 1.4;
  font-weight: 500;
  display: inline-block;
  transition: color 0.15s;
}
.toc__list a:hover {
  color: #e63329;
}
.toc__list li.active a {
  color: #e63329;
  font-weight: 700;
}
.article__intro {
  font-size: 15px;
  color: #444;
  background: #fff5f5;
  border-left: 3px solid #e63329;
  padding: 18px 22px;
  border-radius: 0 10px 10px 0;
  margin-bottom: 48px;
  line-height: 1.8;
}
.article__section {
  margin-bottom: 44px;
}
.article__section h2 {
  font-size: 22px;
  font-weight: 700;
  color: #111;
  margin-bottom: 14px;
  letter-spacing: -0.02em;
}
.article__section p {
  font-size: 15px;
  color: #444;
  margin-bottom: 10px;
}
.article__section ul {
  margin: 10px 0;
  padding-left: 0;
  list-style: none;
}
.article__section ul li {
  font-size: 15px;
  color: #444;
  margin-bottom: 8px;
  padding-left: 18px;
  position: relative;
}
.article__section ul li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 10px;
  width: 6px;
  height: 6px;
  background: #e63329;
  border-radius: 50%;
}
.article__divider {
  border: none;
  border-top: 1px solid #f0f0f0;
  margin: 36px 0;
}
.contact-card {
  background: #f8f8f8;
  border-radius: 12px;
  padding: 28px;
  margin-top: 8px;
}
.contact-card p {
  font-size: 14.5px;
  color: #444;
  margin-bottom: 7px;
}
.contact-card strong {
  color: #111;
}
.contact-card a {
  color: #e63329;
}
.contact-card__note {
  font-size: 13px;
  color: #999;
  margin-top: 14px !important;
}
@media (max-width: 900px) {
  .layout {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  .toc-wrapper {
    order: 2;
  }
  .toc {
    position: static !important;
    width: 100% !important;
    display: none; /* на мобильных меню скрываем */
  }
}
@media (max-width: 600px) {
  .page-hero {
    padding: 48px 20px 40px;
  }
  .page-hero__title {
    font-size: 32px;
  }
  .layout {
    padding: 40px 20px 64px;
  }
  .contact-card {
    padding: 20px;
  }
}
</style>